import type { JSX, ReactNode } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Button, useDisclosure } from '@heroui/react';

import { usePermission } from '~/hooks/usePermission';
import { useOrganizationTeams } from '~/hooks/useTeams';
import { useTenant } from '~/hooks/useTenant';

import { Feedback } from '~/components/Feedback';
import { OrganizationIcon, TeamIcon } from '~/components/Icons';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Skeleton } from '~/components/Skeleton';

import { CreateTeamModal } from './CreateTeamModal';
import { TeamCard } from './TeamCard';

export function OrganizationTeams(): JSX.Element {
  const { currentOrg, loading: tenantLoading } = useTenant();
  const { error, isLoading, teams } = useOrganizationTeams(currentOrg?.id);
  const { canCreateTeam, canManageMembers } = usePermission();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const renderContent = (): ReactNode => {
    if (tenantLoading) {
      return <Feedback description="Loading tenant" icon={<LoadingSpinner />} type={null} />;
    }

    if (isLoading) {
      return <Skeleton type="mini-cards" items={3} />;
    }

    if (!currentOrg) {
      return (
        <Feedback
          type="not-found"
          icon={<OrganizationIcon className="size-16" />}
          description="No organization found"
          title={null}
        />
      );
    }

    if (!canManageMembers) {
      return (
        <Feedback
          type="forbidden"
          description="You need admin or owner permissions to view organization teams."
        />
      );
    }

    if (error) {
      return (
        <Feedback
          description="Failed to load teams. Please try again."
          icon={<TeamIcon className="size-16" />}
          type="error"
        />
      );
    }

    if (!teams.length) {
      return (
        <Feedback
          description="No teams yet."
          icon={<TeamIcon className="size-16" />}
          title={null}
          type="empty"
        >
          {canCreateTeam && (
            <Button color="primary" onPress={onOpen}>
              Create your first team
            </Button>
          )}
        </Feedback>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map(team => (
          <TeamCard key={team.id} team={team} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="OrganizationTeams">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Teams</h2>
          <p className="text-sm">Manage the teams in the organization</p>
        </div>
        {canCreateTeam && (
          <Button color="primary" startContent={<PlusIcon className="w-5 h-5" />} onPress={onOpen}>
            Create Team
          </Button>
        )}
      </div>

      {renderContent()}

      {/* Create Team Modal */}
      <CreateTeamModal isOpen={isOpen} onClose={onClose} />
    </div>
  );
}
